/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\TimeConditionGeneralSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Incoming Call Tools module.
----------------------------------------------------------------*/


import { useMemo } from 'react';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import Toggle from '../../../components/ui/Toggle';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import SelectTimeGroup from '../../../components/ui/SelectTimeGroup';
import { SecretInput } from '../../../components/ui/SecretField';
import type { TimeConditionFormData, TimeGroupOption } from '../formTypes';

interface TimeConditionGeneralSectionProps {
    formData: TimeConditionFormData;
    errors: Record<string, string>;
    timeGroups: TimeGroupOption[];
    loadingTimeGroups: boolean;
    onFieldChange: <K extends keyof TimeConditionFormData>(field: K, value: TimeConditionFormData[K]) => void;
    t: (key: string, opts?: Record<string, unknown>) => string;
}

export function TimeConditionGeneralSection({
    formData, errors, timeGroups, loadingTimeGroups, onFieldChange, t,
}: TimeConditionGeneralSectionProps) {
    const statusOptions = useMemo(() => [
        { value: 'default', label: t('timeConditions.fields.status.options.default') },
        { value: 'forced-match', label: t('timeConditions.fields.status.options.forcedMatch') },
        { value: 'forced-nomatch', label: t('timeConditions.fields.status.options.forcedNomatch') },
    ], [t]);

    return (
        <div className="rounded-lg border border-border bg-card p-6">
            <div className="mb-4">
                <h3 className="text-lg font-semibold">{t('timeConditions.sections.general')}</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">

                {/* Name */}
                <Label htmlFor="name">
                    <SimpleTooltip content={t('timeConditions.fields.name.tooltip')}>
                        <span>
                            {t('timeConditions.fields.name.label')}
                            <span className="ml-1 text-red-500">*</span>
                        </span>
                    </SimpleTooltip>
                </Label>
                <div>
                    <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => onFieldChange('name', e.target.value)}
                        placeholder={t('timeConditions.fields.name.placeholder')}
                        className={errors.name ? 'border-red-500' : ''}
                    />
                    {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
                </div>

                {/* Toggle Code */}
                <Label htmlFor="toggleCode">
                    <SimpleTooltip content={t('timeConditions.fields.toggleCode.tooltip')}>
                        <span>{t('timeConditions.fields.toggleCode.label')}</span>
                    </SimpleTooltip>
                </Label>
                <div>
                    <Input
                        id="toggleCode"
                        value={formData.toggleCode}
                        onChange={(e) => onFieldChange('toggleCode', e.target.value)}
                        placeholder={t('timeConditions.fields.toggleCode.placeholder')}
                    />
                </div>

                {/* Time Group */}
                <Label htmlFor="timeGroup">
                    <SimpleTooltip content={t('timeConditions.fields.timeGroup.tooltip')}>
                        <span>
                            {t('timeConditions.fields.timeGroup.label')}
                            <span className="ml-1 text-red-500">*</span>
                        </span>
                    </SimpleTooltip>
                </Label>
                <div>
                    <SelectTimeGroup
                        value={formData.groupId}
                        onChange={(value) => onFieldChange('groupId', value)}
                        timeGroups={timeGroups}
                        placeholder={loadingTimeGroups ? t('common.loading') : t('timeConditions.fields.timeGroup.placeholder')}
                        error={Boolean(errors.groupId)}
                    />
                    {errors.groupId && <p className="mt-1 text-sm text-red-500">{errors.groupId}</p>}
                </div>

                {/* Auth PIN */}
                <Label htmlFor="authPin">
                    <SimpleTooltip content={t('timeConditions.fields.authPin.tooltip')}>
                        <span>{t('timeConditions.fields.authPin.label')}</span>
                    </SimpleTooltip>
                </Label>
                <div>
                    <SecretInput
                        value={formData.authPin}
                        onChange={(value) => onFieldChange('authPin', value)}
                        placeholder={t('timeConditions.fields.authPin.placeholder')}
                        showCopy={false}
                    />
                </div>

                {/* Status */}
                <Label htmlFor="status">
                    <SimpleTooltip content={t('timeConditions.fields.status.tooltip')}>
                        <span>{t('timeConditions.fields.status.label')}</span>
                    </SimpleTooltip>
                </Label>
                <div>
                    <select
                        id="status"
                        value={formData.status}
                        onChange={(e) => onFieldChange('status', e.target.value as TimeConditionFormData['status'])}
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                        {statusOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>

                {/* BLF Inverted */}
                <Label htmlFor="blfInverted">
                    <SimpleTooltip content={t('timeConditions.fields.blfInverted.tooltip')}>
                        <span>{t('timeConditions.fields.blfInverted.label')}</span>
                    </SimpleTooltip>
                </Label>
                <div>
                    <Toggle
                        value={formData.blfInverted}
                        onChange={(value) => onFieldChange('blfInverted', value)}
                    />
                </div>

            </div>
        </div>
    );
}
